import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import {
  MapPin,
  X,
  Settings,
  Plus,
  Trash2,
  Building2,
  Grid3x3,
  Ruler,
  Sparkles,
  Zap,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { updateSocietyActionPlot } from "../store/slices/societySlice";
import { fetchSocieties } from "../store/slices/societySlice";
import PricingConfiguration from "./PricingConfiguration";

const plotValidationSchema = Yup.object().shape({
  plots: Yup.array().of(
    Yup.object().shape({
      plot_number: Yup.string()
        .required("Plot number is required")
        .matches(
          /^([a-zA-Z]*)(\d+)-?([a-zA-Z]*)(\d+)?$/,
          "Invalid plot number format"
        ),
      block: Yup.string().required("Block is required"),
      size: Yup.string()
        .required("Size is required")
        .test(
          "is-valid-size",
          "Size must be a number or in format like 25*5",
          (value) => {
            if (/^\d+(\.\d+)?$/.test(value)) return true;
            const multiplicationPattern = /^\d+(\.\d+)?\s*\*\s*\d+(\.\d+)?$/;
            return multiplicationPattern.test(value);
          }
        ),
      marla: Yup.string()
        .required("Size in Marla is required")
        .test(
          "is-valid-size",
          "Size must be a number or in format like 25*5",
          (value) => {
            if (/^\d+(\.\d+)?$/.test(value)) return true;
            const multiplicationPattern = /^\d+(\.\d+)?\s*\*\s*\d+(\.\d+)?$/;
            return multiplicationPattern.test(value);
          }
        ),
      plot_type: Yup.string().required("Plot type is required"),
      category: Yup.string().required("Category is required"),
    })
  ),
});

const PlotCreation = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSociety, theme, gradient } = useSidebarContext();
  const [showPricingConfig, setShowPricingConfig] = useState(false);

  const expandPlotRange = (plotInput) => {
    const rangePattern = /^([a-zA-Z]*)(\d+)-([a-zA-Z]*)(\d+)$/;
    const match = plotInput.match(rangePattern);

    if (!match) return [plotInput];

    const [, prefix1, start, prefix2, end] = match;

    if (prefix1 !== prefix2) {
      return [];
    }

    const expandedPlots = [];
    for (let i = parseInt(start); i <= parseInt(end); i++) {
      expandedPlots.push(`${prefix1}${i}`);
    }

    return expandedPlots;
  };

  const handleSubmitPlots = async (values, { resetForm, setSubmitting }) => {
    try {
      if (!activeSociety) {
        enqueueSnackbar("Please select a society first", { variant: "error" });
        setSubmitting(false);
        return;
      }

      const expandedPlots = values.plots.flatMap((plot) => {
        const expandedNumbers = expandPlotRange(plot.plot_number);
        return expandedNumbers.map((num) => ({
          ...plot,
          plot_number: num,
        }));
      });

      const formData = new FormData();
      formData.append("name", activeSociety.name);
      formData.append("location", activeSociety.location);

      formData.append("plotsData", JSON.stringify(expandedPlots));
      if (values.society_image) {
        formData.append("society_image", values.society_image);
      }
      if (values.society_map_image) {
        formData.append("society_map_image", values.society_map_image);
      }

      await dispatch(
        updateSocietyActionPlot({
          id: activeSociety._id,
          data: formData,
        })
      ).unwrap();

      await dispatch(fetchSocieties());
      enqueueSnackbar("Plots added successfully!", { variant: "success" });
      resetForm();
      setSubmitting(false);
    } catch (error) {
      console.error("Error adding plots:", error);
      enqueueSnackbar("Failed to add plots", { variant: "error" });
      setSubmitting(false);
    }
  };

  if (!activeSociety) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div
          className={`relative ${gradient} rounded-3xl shadow-2xl p-12 text-center max-w-lg border ${theme.tailwind.border6}`}
        >
          <div className="absolute inset-0 bg-white/5 backdrop-blur-3xl rounded-3xl"></div>
          <div className="relative">
            <div
              className={`w-20 h-20 mx-auto mb-6 rounded-2xl ${theme.tailwind.bg18} flex items-center justify-center shadow-xl`}
            >
              <MapPin className="w-10 h-10 text-white" />
            </div>
            <h2 className={`text-3xl font-bold ${theme.tailwind.text9} mb-3`}>
              No Society Selected
            </h2>
            <p className={`${theme.tailwind.text9} opacity-60`}>
              Please select a society from sidebar to get started
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Glass Header with Animated Gradient */}
        <div
          className={`relative ${gradient} rounded-3xl border ${theme.tailwind.border6} overflow-hidden shadow-2xl`}
        >
          {/* Glass Effect Layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-2xl"></div>

          <div className="relative p-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Zap
                    className={`w-5 h-5 ${theme.tailwind.text10} animate-pulse`}
                  />
                  <span
                    className={`${theme.tailwind.text10} text-xs font-bold uppercase tracking-widest`}
                  >
                    Expand Your Society
                  </span>
                </div>
                <h1
                  className={`text-5xl font-black ${theme.tailwind.text9} tracking-tight`}
                >
                  Plot Creation
                </h1>
              </div>

              <div
                className={`relative inline-flex items-center gap-3 px-6 py-3 rounded-2xl ${gradient} border-2 ${theme.tailwind.border27} shadow-2xl overflow-hidden`}
              >
                <div className="absolute inset-0 bg-white/5 backdrop-blur-xl"></div>
                <div
                  className={`relative w-10 h-10 rounded-xl ${theme.tailwind.bg18} flex items-center justify-center shadow-lg`}
                >
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="relative">
                  <p
                    className={`${theme.tailwind.text9} opacity-60 text-xs font-medium`}
                  >
                    Active Society
                  </p>
                  <p className={`${theme.tailwind.text9} font-bold text-lg`}>
                    {activeSociety.name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <Formik
          initialValues={{
            plots: [
              {
                plot_number: "",
                block: "",
                plot_type: "",
                size: "",
                marla: "",
                category: "",
              },
            ],
          }}
          validationSchema={plotValidationSchema}
          onSubmit={handleSubmitPlots}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form className="space-y-5">
              <FieldArray name="plots">
                {({ remove, push }) => (
                  <>
                    {values.plots.map((plot, index) => (
                      <div
                        key={index}
                        className={`relative group ${gradient} rounded-2xl border ${theme.tailwind.border6} overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500`}
                      >
                        {/* Glass Morphism Effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-xl"></div>

                        {/* Hover Glow Effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

                        {/* Header */}
                        <div
                          className={`relative flex items-center justify-between px-6 py-4 border-b ${theme.tailwind.border6}`}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-12 h-12 rounded-xl ${theme.tailwind.bg18} flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}
                            >
                              <Building2 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p
                                className={`${theme.tailwind.text9} opacity-50 text-xs font-medium`}
                              >
                                Plot Entry
                              </p>
                              <h3
                                className={`text-xl font-bold ${theme.tailwind.text9}`}
                              >
                                #{index + 1}
                              </h3>
                            </div>
                          </div>

                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="relative p-2.5 rounded-xl bg-red-500/10 hover:bg-red-500 border border-red-500/30 hover:border-red-500 transition-all duration-300 group/btn"
                              aria-label="Remove plot"
                            >
                              <Trash2
                                size={18}
                                className="text-red-500 group-hover/btn:text-white transition-colors"
                              />
                            </button>
                          )}
                        </div>

                        {/* Form Grid */}
                        <div className="relative p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {/* Plot Number */}
                            <div className="space-y-2">
                              <label
                                className={`flex items-center gap-2 ${theme.tailwind.text9} font-semibold text-sm`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-lg ${theme.tailwind.bg18} flex items-center justify-center shadow-md`}
                                >
                                  <Grid3x3 size={12} className="text-white" />
                                </div>
                                Plot Number Range
                              </label>
                              <div className="relative">
                                <Field
                                  type="text"
                                  name={`plots.${index}.plot_number`}
                                  className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border28} rounded-xl px-4 py-3 text-sm focus:ring-2 ${theme.tailwind.focusRing8} focus:border-transparent transition-all duration-300 hover:shadow-lg focus:shadow-xl`}
                                  placeholder="101-110, AR-3430-AR-3440"
                                />
                              </div>
                              {errors.plots?.[index]?.plot_number &&
                                touched.plots?.[index]?.plot_number && (
                                  <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                                    <span className="animate-pulse">⚠</span>
                                    {errors.plots[index].plot_number}
                                  </p>
                                )}
                            </div>

                            {/* Block */}
                            <div className="space-y-2">
                              <label
                                className={`flex items-center gap-2 ${theme.tailwind.text9} font-semibold text-sm`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-lg ${theme.tailwind.bg18} flex items-center justify-center shadow-md`}
                                >
                                  <Grid3x3 size={12} className="text-white" />
                                </div>
                                Block
                              </label>
                              <Field
                                type="text"
                                name={`plots.${index}.block`}
                                className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border28} rounded-xl px-4 py-3 text-sm focus:ring-2 ${theme.tailwind.focusRing8} focus:border-transparent transition-all duration-300 hover:shadow-lg focus:shadow-xl`}
                                placeholder="Enter block"
                              />
                              {errors.plots?.[index]?.block &&
                                touched.plots?.[index]?.block && (
                                  <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                                    <span className="animate-pulse">⚠</span>
                                    {errors.plots[index].block}
                                  </p>
                                )}
                            </div>

                            {/* Size */}
                            <div className="space-y-2">
                              <label
                                className={`flex items-center gap-2 ${theme.tailwind.text9} font-semibold text-sm`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-lg ${theme.tailwind.bg18} flex items-center justify-center shadow-md`}
                                >
                                  <Ruler size={12} className="text-white" />
                                </div>
                                Size
                              </label>
                              <Field
                                type="text"
                                name={`plots.${index}.size`}
                                className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border28} rounded-xl px-4 py-3 text-sm focus:ring-2 ${theme.tailwind.focusRing8} focus:border-transparent transition-all duration-300 hover:shadow-lg focus:shadow-xl`}
                                placeholder="25*50 or 1250"
                              />
                              {errors.plots?.[index]?.size &&
                                touched.plots?.[index]?.size && (
                                  <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                                    <span className="animate-pulse">⚠</span>
                                    {errors.plots[index].size}
                                  </p>
                                )}
                            </div>

                            {/* Marla */}
                            <div className="space-y-2">
                              <label
                                className={`flex items-center gap-2 ${theme.tailwind.text9} font-semibold text-sm`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-lg ${theme.tailwind.bg18} flex items-center justify-center shadow-md`}
                                >
                                  <Ruler size={12} className="text-white" />
                                </div>
                                Marla
                              </label>
                              <Field
                                type="text"
                                name={`plots.${index}.marla`}
                                className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border28} rounded-xl px-4 py-3 text-sm focus:ring-2 ${theme.tailwind.focusRing8} focus:border-transparent transition-all duration-300 hover:shadow-lg focus:shadow-xl`}
                                placeholder="Enter marla"
                              />
                              {errors.plots?.[index]?.marla &&
                                touched.plots?.[index]?.marla && (
                                  <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                                    <span className="animate-pulse">⚠</span>
                                    {errors.plots[index].marla}
                                  </p>
                                )}
                            </div>

                            {/* Plot Type */}
                            <div className="space-y-2">
                              <label
                                className={`flex items-center gap-2 ${theme.tailwind.text9} font-semibold text-sm`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-lg ${theme.tailwind.bg18} flex items-center justify-center shadow-md`}
                                >
                                  <Building2 size={12} className="text-white" />
                                </div>
                                Plot Type
                              </label>
                              <Field
                                as="select"
                                name={`plots.${index}.plot_type`}
                                className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border28} rounded-xl px-4 py-3 text-sm focus:ring-2 ${theme.tailwind.focusRing8} focus:border-transparent transition-all duration-300 hover:shadow-lg focus:shadow-xl cursor-pointer`}
                              >
                                <option value="">Select type</option>
                                <option value="Residential">Residential</option>
                                <option value="Commercial">Commercial</option>
                              </Field>
                              {errors.plots?.[index]?.plot_type &&
                                touched.plots?.[index]?.plot_type && (
                                  <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                                    <span className="animate-pulse">⚠</span>
                                    {errors.plots[index].plot_type}
                                  </p>
                                )}
                            </div>

                            {/* Category */}
                            <div className="space-y-2">
                              <label
                                className={`flex items-center gap-2 ${theme.tailwind.text9} font-semibold text-sm`}
                              >
                                <div
                                  className={`w-6 h-6 rounded-lg ${theme.tailwind.bg18} flex items-center justify-center shadow-md`}
                                >
                                  <Grid3x3 size={12} className="text-white" />
                                </div>
                                Category
                              </label>
                              <Field
                                as="select"
                                name={`plots.${index}.category`}
                                className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border28} rounded-xl px-4 py-3 text-sm focus:ring-2 ${theme.tailwind.focusRing8} focus:border-transparent transition-all duration-300 hover:shadow-lg focus:shadow-xl cursor-pointer`}
                              >
                                <option value="">Select category</option>
                                <option value="General">General</option>
                                <option value="Corner">Corner</option>
                                <option value="Park Face">Park Face</option>
                                <option value="Boulevard">Boulevard</option>
                              </Field>
                              {errors.plots?.[index]?.category &&
                                touched.plots?.[index]?.category && (
                                  <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                                    <span className="animate-pulse">⚠</span>
                                    {errors.plots[index].category}
                                  </p>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Compact Action Buttons - Left Aligned */}
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          push({
                            plot_number: "",
                            block: "",
                            plot_type: "",
                            size: "",
                            marla: "",
                            category: "",
                          })
                        }
                        className={`group relative px-5 py-2.5 ${theme.tailwind.bg53} ${theme.tailwind.text9} rounded-xl ${theme.tailwind.bgHover30} border ${theme.tailwind.border} font-semibold transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 overflow-hidden`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        <div className="relative flex items-center gap-2">
                          <Plus
                            size={18}
                            className="group-hover:rotate-90 transition-transform duration-300"
                          />
                          <span>Add More</span>
                        </div>
                      </button>
                      <button
                        type="submit"
                        className={`group relative px-5 py-2.5 ${theme.tailwind.bg18} text-white rounded-xl ${theme.tailwind.bgHover25} font-semibold transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 overflow-hidden`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        <span className="relative">Submit Plots</span>
                      </button>
                    </div>
                  </>
                )}
              </FieldArray>
            </Form>
          )}
        </Formik>

        {/* Glass Pricing Configuration */}
        <div
          className={`relative ${gradient} rounded-3xl border ${theme.tailwind.border6} overflow-hidden shadow-2xl`}
        >
          {/* Glass Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-2xl"></div>

          <div className="relative p-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-xl ">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3
                      className={`text-2xl font-black ${theme.tailwind.text9}`}
                    >
                      Pricing Configuration
                    </h3>
                    <p className={`${theme.tailwind.text9} opacity-60 text-sm`}>
                      Set pricing for different categories & sizes
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowPricingConfig(true)}
                className="group relative px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-bold shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <div className="relative flex items-center gap-2">
                  <Settings
                    size={20}
                    className="group-hover:rotate-180 transition-transform duration-500"
                  />
                  <span>Configure</span>
                </div>
              </button>
            </div>

            <div
              className={`relative mt-6 p-5 ${theme.tailwind.bg5} rounded-2xl border ${theme.tailwind.border6} overflow-hidden`}
            >
              <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
              <div className="relative flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-xl ${theme.tailwind.bg18} flex items-center justify-center flex-shrink-0 shadow-lg`}
                >
                  <Sparkles className="w-5 h-5 text-white animate-pulse" />
                </div>
                <div>
                  <p className={`${theme.tailwind.text9} font-semibold mb-1`}>
                    Smart Auto-Pricing
                  </p>
                  <p className={`${theme.tailwind.text9} opacity-70 text-sm`}>
                    Prices automatically calculated based on your configuration
                    settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Configuration Modal */}
      {showPricingConfig && (
        <PricingConfiguration
          society={activeSociety}
          onSave={(pricing) => {
            setShowPricingConfig(false);
            enqueueSnackbar("Pricing configuration updated!", {
              variant: "success",
            });
          }}
          onClose={() => setShowPricingConfig(false)}
        />
      )}
    </div>
  );
};

export default PlotCreation;
